import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Create axios instance with default configuration
const axiosInstance = axios.create({
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authorization header
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Get token from localStorage
    const token = localStorage.getItem('CMS_USER_AUTH_TOKEN');

    if (token && config.headers) {
      config.headers['Authorization'] = token;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Clear auth data and redirect to login
      localStorage.clear();
      window.location.href = '/';
      return Promise.reject(new Error('Unauthorized - please login again'));
    }

    // Handle 403 Forbidden errors
    if (error.response?.status === 403) {
      console.error('Access forbidden - insufficient permissions');
      return Promise.reject(new Error('Access forbidden'));
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error - please check your connection');
      return Promise.reject(new Error('Network error'));
    }

    // Handle other errors
    const errorData = error.response?.data as any;
    const errorMessage = errorData?.message || error.message || 'An error occurred';
    console.error('API Error:', errorMessage);

    return Promise.reject(error);
  }
);

// Function to set auth token (for backward compatibility)
export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('CMS_USER_AUTH_TOKEN', token);
    axiosInstance.defaults.headers.common['Authorization'] = token;
  } else {
    localStorage.removeItem('CMS_USER_AUTH_TOKEN');
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

// Function to get current auth token
export const getAuthToken = (): string | null => {
  return localStorage.getItem('CMS_USER_AUTH_TOKEN');
};

// Function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  if (!token) return false;

  try {
    // Basic JWT token validation (check if it's not expired)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime;
  } catch {
    return false;
  }
};

export default axiosInstance;